import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { AppRouterService } from 'app/services/app/router.service';
import { AppService } from '@app/services/app/app.service';
import { VersionsViewService } from '@app/services/app/versions.service';
import { AnnotationsService } from '@core/services/annotations/annotations.service';
import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';

import * as Objects from '@core/objects';
// core recorder state 
import * as fromState from '@core/store/state/recorder/recorder.state';
import { RecorderService } from '@core/services/recorder/recorder.service';

import { VersionFeedback } from '@core/models/version-feedback';
import { VersionShare, VersionShareType, VersionShareEntryDetail, VersionShareOptionName, getVersionShareOption, isVersionShareOpen } from '@core/models/version-share.model';
import { VersionShareFeedbackMessage, VersionShareFeedbackMessageType } from '@core/models/version-share-message.model';
import { VersionMessageContext } from '@core/constants/version-message-context.constants';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';

import * as flContexts from '@core/constants/files-list-contexts.constants';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';

@Component({
    selector: 'version-details-content-messages-version-share-feedback',
    templateUrl: './messages-version-share-feedback.component.html',
    styleUrls: ['./messages-version-share-feedback.component.scss']
})
export class VersionsDetailsMessagesVersionShareFeedbackComponent implements OnInit, OnDestroy, OnChanges {
    public readonly VERSION_SHARE_MESSAGE_TYPE = VersionShareFeedbackMessageType;
    // msg available contexts: used to select and filter the messages to display
    public readonly VERSION_MESSAGE_CONTEXT = VersionMessageContext;

    @Input() version: Objects.Version;
    // @deprecated oct-2026: legacy share feedback bubbles url call response, the open status now comes from myShare
    // @Input() feedbacks: Array<VersionFeedback>;
    @Input() versionShares: Array<VersionShare> = null;
    @Input() versionShareMessages: Array<VersionShareFeedbackMessage> = null;
    // @deprecated oct-2026: legacy share feedback url call response, replaced by myShare
    // @Input() share: any = null;
    // oct-2026: current user's open approval/feedback entry on this version (null when not a recipient)
    @Input() myShare: VersionShareEntryDetail = null;
    // REMINDER: share.options (are boolean)
    // {allow_see,get_approval,get_comments,get_signature}

    public user: Objects.User = null;
    public openAttachmentsMessageIds: Set<string> = new Set<string>();
    public uploads: Array<Objects.Version>;
    public messagesList: Array<VersionShareFeedbackMessage> = [];
    public prefixUrl: string = '';
    public prefixDeleteUrl: string = '';
    public messageAddUrl: string = '';
    public scope: string = flContexts.CTX.MESSAGE_FEEDBACK;
    public msgActionAccess: boolean = false;
    public hash: string = '';

    // Notes management
    public notesCounterMap: { [messageId: number]: Objects.AnnotationsCounter[] } = {};
    public loadingNotesMap: { [messageId: number]: boolean } = {};

    // File menu for signed files list
    public fileMenu: Array<ThreeDotsMenuItem> = [
        {
            title: "Download File",
            key: "download",
            icon: "download"
        }
    ];

    // public versionShareMessages: Array<VersionShareFeedbackMessage> = null;
    public feedbackTextMessages: Array<Objects.VersionMessage> = null;
    
    protected messagesSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected recorderSubscriptor: Subscription;
    protected iListenerSubs: Subscription;
    protected uploadsSubscriptor: Subscription;

    public menuVersion: Array<ThreeDotsMenuItem> = [{
        title: "See Artwork Details",
        key: "details",
        icon: "see-details"
    }];

    // @deprecated oct-2026: the open status now comes from myShare
    // protected userFeedback: VersionFeedback = null;

    constructor(private appRouter: AppRouterService,
        public appSrv: AppService,
        private versionsViewSrv: VersionsViewService,
        private recorderSrv: RecorderService,
        private iListener: InterfaceListenerService,
        private annotationsSrv: AnnotationsService,
        private cdr: ChangeDetectorRef,
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService
    ) { }

    ngOnInit() {        
        this.appSubscriptor = this.appSrv.getSession().subscribe(session => {
            this.user = session.current.user;
            // this.updateUserApproval(); // @deprecated oct-2026
            this.updateUserAccess();
        });

        this.messagesSubscriptor = this.versionsViewSrv.selectComments().subscribe((messages: Array<Objects.VersionMessage>) => {
            // Here we filter the messages which have context to get the messages given on the feedback (2023/09/08context pattern ==> feedback-{id_feedback})
            if (messages != null){
                this.feedbackTextMessages = messages.filter((m:Objects.VersionMessage) => 
                            m.context.startsWith(this.VERSION_MESSAGE_CONTEXT.FEEDBACK) || 
                            m.context.startsWith(this.VERSION_MESSAGE_CONTEXT.UPLOAD_VERSION) );
                this.updateMessageList();
                // Load notes for all feedback messages
                this.feedbackTextMessages.forEach(msg => this.loadNotesForMessage(msg.message));
            }
        });

        this.uploadsSubscriptor = this.versionsViewSrv.selectUploadedVersionUploads().subscribe((uploads: Objects.Version[]) =>{
            this.uploads = uploads;
        });
        
        // here we check the recorder status to reload messages
        this.recorderSubscriptor = this.recorderSrv.getStatus().subscribe((status: fromState.RecorderStatus ) => {
            if(status == fromState.RecorderStatus.FINISHED_SUCCESS){
                this.versionsViewSrv.loadComments();
            }
        });

        this.iListenerSubs = this.iListener.getVersionFeedbackDone().subscribe((version: Objects.Version) => {
            // Handle the version feedback 'onDone' event here to reload the comments.
            if(this.version != null && version!=null && version.id == this.version.id){
                this.versionsViewSrv.loadComments();
                this.updateUserAccess();
            }
        });

    }

    ngOnDestroy() {
        this.messagesList = null;
        this.appSubscriptor.unsubscribe();
        this.messagesSubscriptor.unsubscribe();
        this.recorderSubscriptor.unsubscribe();
        this.iListenerSubs.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // @deprecated oct-2026: legacy share feedback bubbles url call, the open status now comes from myShare
        // if(changes.feedbacks){ // TODO maybe remove
        //     this.updateMessageList();
        //     this.updateUserApproval();
        //     this.updateUserAccess();
        // }

        if(changes.versionShareMessages && this.versionShareMessages != null){
            this.updateMessageList();
        }

        // Here now we allow save message on feedback through /share using hash and context on messagesa and files
        // and only let user add message and notes when feedbacks is still pending
        // set msgActionAccess according share.options, feedback status and user logged to let the river flows
        if(changes.myShare){
            // the entry can disappear once it is closed: access has to be recalculated either way
            this.updateUserAccess();
            if(this.myShare?.entry != null){
                this.hash = this.myShare.entry.token;
                // jan 2026: update add message url without /p/ for add message through share feedback
                this.messageAddUrl = ("/web/share/feedback/"+this.myShare.entry.token+"/versions/"+this.version.id+"/messages");
                this.prefixDeleteUrl = ("/web/share/p/feedback/"+this.myShare.entry.token);
            }
        } 
        this.prefixUrl = ("/p/feedback/versions/"+this.version.id); // only for get legacy notes not for save

    }

    onVersionMenuClick(menu: VersionListItemMenuClick) {
        // console.debug(" menu original click",menu);
        switch(menu.key) {
            case "details":
                // url to go to Artwork Details
                this.appRouter.navigate( "/versions/"+menu.version.id);
                break;
        }
    }

    onClickVersionPreview(file: Objects.File, version: Objects.Version){
        if (file != null && version != null){
            this.versionsViewSrv.clear();
            this.appRouter.navigate('/versions/'+version.id + "/files/"+file.id);
        }
    }


    onUpdateMessages(message: Objects.Message) {
        this.versionsViewSrv.loadComments();
    }

    onDeleteMessage(message: Objects.Message) {
        this.versionsViewSrv.loadComments();
    }

    toggleAttachments(message: any, itemKey?: string | number): void {
        if (!itemKey && (!message || typeof message !== 'object' || !message.id)) {
            return;
        }

        const messageKey = itemKey != null ? String(itemKey) : String(message.id);

        if (this.openAttachmentsMessageIds.has(messageKey)) {
            this.openAttachmentsMessageIds.delete(messageKey);
        } else {
            this.openAttachmentsMessageIds.add(messageKey);
        }
    }

    isAttachmentsOpen(message: any, itemKey?: string | number): boolean {
        const messageKey = itemKey != null ? String(itemKey) : (message && typeof message === 'object' && message.id ? String(message.id) : '');
        return !!messageKey && this.openAttachmentsMessageIds.has(messageKey);
    }

    protected updateMessageList() : void {
        if(this.feedbackTextMessages != null && this.versionShareMessages != null){
            let msgList: VersionShareFeedbackMessage[]  =  this.feedbackTextMessages.map((msg: Objects.VersionMessage) => {

                let shares = (this.versionShares.length > 0) ? this.versionShares.filter((vs:VersionShare) => vs.entry.user.id == msg.message.user.id) : [];
                return {
                    // Stable per-message id (not uuidv4()) so trackBy keeps the same
                    // <li>/core-signed-files-list instance across comments reloads,
                    // preserving its grid/list view state.
                    uuid: 'text-' + msg.message.id,
                    type: this.VERSION_SHARE_MESSAGE_TYPE.TEXT_MESSAGE_TYPE,
                    message: msg.message, 
                    user_version_shares: shares, 
                    date: msg.message.date,
                    version: this.version
                };
            });

            this.messagesList = [...this.versionShareMessages, ...msgList];
        }
    }

    // @deprecated oct-2026: legacy share feedback bubbles url call, the open status now comes from myShare
    // protected updateUserApproval() : void {
    //     if(this.feedbacks == null || this.user == null)
    //         return;
    //     
    //     let userFeedbacks = this.feedbacks.filter((f:VersionFeedback) => f.user.id == this.user.id)
    //     this.userFeedback = userFeedbacks.length > 0 ? userFeedbacks[0] : null;
    // }
    // need to check both user and sahre to manage current user access
    protected updateUserAccess() : void {
        if(this.user != null && this.myShare != null){
            this.msgActionAccess = (getVersionShareOption(this.myShare, VersionShareOptionName.GET_COMMENTS) &&
                                this.myShare.entry.user.id == this.user.id &&
                                isVersionShareOpen(this.myShare)
                            );
        } else {
            this.msgActionAccess = false;
        }
        
    }

    protected getVersionUpload(message: VersionShareFeedbackMessage) : Objects.Version | null {
        return this.uploads.find((v: Objects.Version) => v.id.toString() == message.version_share.options.id_version.toString());
    }

    /**
     * Load annotation counter for a specific message
     */
    private loadNotesForMessage(message: Objects.Message): void {
        if (!message || !message.id || !(message.attachments?.length > 0 || message.files?.length > 0)) {
            return;
        }

        // Skip if already loading or already loaded
        if (this.loadingNotesMap[message.id]) {
            return;
        }

        const counterUrlPrefix = '/messages/' + message.id;
        this.loadingNotesMap[message.id] = true;

        this.annotationsSrv.getCounter(counterUrlPrefix).subscribe({
            next: (counter: Objects.AnnotationsCounter[]) => {
                this.notesCounterMap[message.id] = [...counter];
                this.loadingNotesMap[message.id] = false;
                this.cdr.detectChanges();
            },
            error: (error: HttpErrorResponse) => {
                console.debug('[VersionsDetailsMessagesVersionShareFeedbackComponent] Error getting message notes counter:', error);
                this.notesCounterMap[message.id] = [];
                this.loadingNotesMap[message.id] = false;
            }
        });
    }

    /**
     * Get notes for a specific message
     */
    getNotesForMessage(message: Objects.Message): Objects.AnnotationsCounter[] {
        return message && message.id ? (this.notesCounterMap[message.id] || []) : [];
    }

    /**
     * Handle file menu click from core-signed-files-list (download/notes)
     */
    onFileMenuClick(menuItem: AmsFilesListItemMenuClick): void {
        switch (menuItem.key) {
            case 'download':
                this.fileDownloadSrv.downloadFile(menuItem.file);
                break;
        }
    }

    /**
     * Handle file icon download click from core-signed-files-list
     */
    onFileIconDownloadClick(file: Objects.AmsFile): void {
        this.fileDownloadSrv.downloadFile(file);
    }

    /**
     * Handle file signature refresh from core-signed-files-list component
     * Reload all message comments to get fresh file signatures
     */
    onRefreshSignaturesNeeded(): void {
        this.versionsViewSrv.loadComments();
    }


    /**
     * Handle notes saved event from core-pdf-annotations-viewer to reload counter after saving notes.
     */
    onNotesSaved(message: Objects.Message, event: any) {
        // After saving a note, refresh the notes counter for this message
        this.loadNotesForMessage(message);
    }

    /**
     * TrackBy function for messages list
     */
    trackByMessageId(index: number, msg: any): string {
        if (!msg) {
            return '';
        }

        if (msg.uuid) {
            return msg.uuid;
        }

        if (msg.id) {
            return msg.id.toString();
        }

        return msg.message?.id ? msg.message.id.toString() : '';
    }
}
